import { Checkbox } from '@/components/common/Checkbox';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import { ekDomain, EMAIL_VALIDATE_REGEX, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { COMMON_TYPE } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { isAdmin, isEnduser, isOwner } from '@/utils/utils';
import { URL_CONFIG } from '@/configs/environment';
import AnimateButton from '@/components/general/button-custom/AnimateButton';

export default observer(function SignInPage() {
    // hooks
    const { t } = useTranslation();

    return (
        <div>
            <h3 className='title-1 text-center'>{t('words_title.sign_in')}</h3>
            <div className='mx-auto flex flex-col justify-center'>
                <SignInForm />
            </div>
        </div>
    );
});

const SignInForm = observer(() => {
    // hooks
    const { t } = useTranslation();
    const { pathname, state } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        authStore: { signIn }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email'),
        password: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        keepme: yup
            .boolean()
            .default(true)
            .optional()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            email: '',
            password: '',
            keepme: true
        }
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);

    // lifeCycle
    useEffect(() => {
        if (state?.email) form.setValue('email', state?.email);
    }, []);

    // function
    const onSubmit = async (data: FormData) => {
        const { email, password, keepme } = data;
        const res = await flowResult(
            signIn({
                email,
                password,
                type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
                keepme
            })
        );

        if (!res) return;

        const { user } = res;
        const name = user.fullname;
        const token = res.token?.replace('Bearer ', '');

        const domain = isEk ? `${URL_CONFIG.APP.url}/${ekDomain}` : `${URL_CONFIG.APP.url}`;
        let url_return = '';

        // navigate after login
        const currentHref = state?.previousUrl;
        if (currentHref?.trim()?.length > 0 && !currentHref?.includes(`${ROUTES.signin.href}`)) {
            url_return = `${currentHref}`;
        } else {
            if (isEnduser(Number(user?.roleId))) {
                url_return = isEk ? `${URL_CONFIG.DOMAIN_WORDPRESS.url}${ekDomain}` : `${URL_CONFIG.DOMAIN_WORDPRESS.url}`;
            } else if (isAdmin(Number(user?.roleId))) {
                url_return = `${domain}${ROUTES.dashboard.href}`;
            } else if (isOwner(Number(user?.roleId))) {
                url_return = `${domain}${ROUTES.bikeManagement.href}`;
            }
        }

        let link = '';

        if (isEk) {
            link = `${url_return}`;
        } else {
            link = `${ROUTES.home.href}wp-json/custom/v1/set-cookie?keepme=${keepme}&name=${name}&url_return=${url_return}&token=${token}`;
        }

        window.location.href = `${link}`;
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='mt-8'>
                <div className='flex flex-col gap-4'>
                    <FormField
                        control={form.control}
                        name='email'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.email')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('field.email')}
                                        isHalfWidth
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            form.clearErrors('email');
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='password'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.password')}</FormLabel>
                                <FormControl>
                                    <PasswordInput
                                        type={!showPassword ? 'password' : 'text'}
                                        placeholder={t('field.password')}
                                        onToggleIcon={() => setShowPassword(!showPassword)}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            form.clearErrors('password');
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex justify-end mt-4'>
                    <Link
                        to={
                            isEk ?
                                `/${ekDomain}${ROUTES.forgetPassword.href}` :
                                ROUTES.forgetPassword.href
                        }
                        className='link-1'
                    >
                        {t('sentences.forgot_password')}
                    </Link>
                </div>
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='keepme'
                        render={({ field }) => (
                            <FormItem className='flex items-center'>
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <FormLabel className='font-normal text-text-foreground ml-1.5 mb-0 cursor-pointer'>
                                    {t('sentences.keep_me')}
                                </FormLabel>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex justify-center'>
                    <AnimateButton
                        type='submit'
                        outline
                        contentComp={t('words_title.sign_in')}
                        parentClassName='mt-8 w-[208px] h-[48px] px-1'
                        textClassName='!pr-0 !text-fs-mid14'
                        iconArrowClassName='!hidden'
                    />
                </div>

                <div className='text-center text-text-foreground mt-8'>
                    <Trans
                        i18nKey='sentences.register_here'
                        components={{
                            Link: (
                                <Link
                                    to={isEk ? `/${ekDomain}${ROUTES.signup.href}` : ROUTES.signup.href}
                                    className='link-1'
                                />
                            )
                        }}
                    />
                </div>

            </form>
        </Form>
    );
});
